<script setup lang="ts">
import type { Schemas } from "#shopware"

const props = defineProps<{
    content: Schemas["CmsBlock"];
    count?: number;
}>()

const { backgroundStyles } = useCmsBackgroundStyles(props.content)
const { marginStyles } = useCmsMarginStyles(props.content)
const { getCmsBlockName, getCmsElementName } = useCms()
const { getElementComponent, getBlockComponent } = useCmsComponentRegistry()
// Spacing for blocks with a background color
const hasBackgroundColor = computed(() => props.content.backgroundColor != null)

const component = shallowRef()
const blockWrapper = ref<HTMLElement>()

// Determine component name (block or element)
const compName = computed(() => {
    let name = getCmsBlockName(props.content.type)

    if (props.content.slots.length === 1 && props.content.slots[0]?.type) {
        name = getCmsElementName(props.content.slots[0].type)
    }

    return name
})

const isHeaderOrFooter = computed(() => {
    return props.content.type === 'a4g_header' || props.content.type === 'a4g_footer'
})

// Load component based on critical status
const loadComponent = () => {
    const name = compName.value

    // Check if it's a single-slot element and render element component directly 
    if (props.content.slots.length === 1 && props.content.slots[0]?.type) {
        component.value = getElementComponent(name)
        if (!component.value) {
            console.warn(`[StructureBlock] Element not found: ${name}`)
        }
        return
    }

    component.value = getBlockComponent(name)

    if (!component.value) {
        console.warn(`[StructureBlock] Component not found: ${name}`)
    }
}

loadComponent()
</script>

<template>
    <div 
        ref="blockWrapper" 
        class="structure-block w-full" 
        :class="{
            [content.cssClass ?? '']: content.cssClass,
            'block': content.visibility?.mobile,
            'hidden': !content.visibility?.mobile,
            'md:block': content.visibility?.tablet,
            'md:hidden': !content.visibility?.tablet,
            'xl:block': content.visibility?.desktop,
            'xl:hidden': !content.visibility?.desktop,
            'max-w-screen-sm mx-auto': content?.customFields?.maxWidthCssClass != null && content?.customFields?.maxWidthCssClass === 'sm',
            'max-w-screen-md mx-auto': content?.customFields?.maxWidthCssClass != null && content?.customFields?.maxWidthCssClass === 'md',
            'max-w-screen-lg mx-auto': content?.customFields?.maxWidthCssClass != null && content?.customFields?.maxWidthCssClass === 'lg',
            'max-w-screen-xl mx-auto': content?.customFields?.maxWidthCssClass != null && content?.customFields?.maxWidthCssClass === 'xl',
            'max-w-screen-2xl mx-auto': content?.customFields?.maxWidthCssClass != null && content?.customFields?.maxWidthCssClass === '2xl',
            'mb-12 md:mb-16': !isHeaderOrFooter,
            'px-4 py-3 md:px-6 md:py-4': hasBackgroundColor
        }"
        :style="[marginStyles, backgroundStyles]"
    >
        <component
            :is="component"
            v-if="component"
            :content="content.slots.length === 1 ? content.slots[0] : content"
        />
    </div>
</template>
